import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Activity } from 'lucide-react'
import { useI18n } from '../../i18n'
import { useProfileStore } from '../../stores'
import ProfileSubpageLayout from '../../components/ProfileSubpageLayout'

export default function ProfileSocialLifestyle() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { profile, updateProfile, syncSection } = useProfileStore()
  
  const [form, setForm] = useState({
    smoking: '', smokingFrequency: '',
    alcohol: '', alcoholFrequency: '',
    cannabis: '', cannabisFrequency: '', cannabisMethod: '',
    exercise: '', occupation: '',
  })

  useEffect(() => {
    // ★ v242.150.0 SOCIAL-EXERCISE-VOCAB-1 — signup used to write 'None' /
    //   'Sometimes' / 'Regular', which match no <option> here, so the answer a
    //   patient had already given rendered as "Select…". Mapped on READ: no data
    //   migration, and no second writer inventing a value.
    if (profile?.socialLifestyle) {
      const LEGACY_EXERCISE = {
        None: 'Never',
        Sometimes: '1-2 times per week',
        Regular: '3-4 times per week',
      }
      const s = { ...profile.socialLifestyle }
      if (s.exercise && LEGACY_EXERCISE[s.exercise]) s.exercise = LEGACY_EXERCISE[s.exercise]
      setForm(prev => ({ ...prev, ...s }))
    }
  }, [profile])

  const handleSave = async () => {
    const data = { ...form, completed: true }
    const result = await syncSection('socialLifestyle', data)
    if (!result.success) updateProfile({ socialLifestyle: data })
    navigate('/profile')
  }

  const inputCls = "w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] bg-white transition-colors"

  // Three-way pill component
  const ThreeWayPills = ({ value, onChange, options }) => (
    <div className="flex gap-2.5 mb-3">
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)}
          className={`flex-1 py-2.5 rounded-[14px] text-[13px] font-semibold transition-all ${value === opt.value ? 'bg-[#46A8F9] text-white' : 'bg-gray-50 text-gray-400 border-[1.5px] border-gray-200'}`}>
          {opt.label}
        </button>
      ))}
    </div>
  )

  return (
    <ProfileSubpageLayout sectionId="social-lifestyle" icon={Activity} onSave={handleSave} saving={false} canSave={true}>
      {/* Smoking */}
      <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileSocial.tobaccoUse')}</p>
      
      <ThreeWayPills value={form.smoking} onChange={(v) => setForm({...form, smoking: v, smokingFrequency: v !== 'Yes' ? '' : form.smokingFrequency})}
        options={[{ value: 'Yes', label: t('profileSocialLifestyle.yes') }, { value: 'No', label: t('profileSocialLifestyle.no') }, { value: 'Former', label: t('profileSocialLifestyle.former') }]} />
      
      {form.smoking === 'Yes' && (
        <div className="mb-4">
          <label className="block text-[12px] text-gray-500 mb-1">{t('profileSocialLifestyle.howOften')}</label>
          <select value={form.smokingFrequency} onChange={(e) => setForm({...form, smokingFrequency: e.target.value})} className={inputCls} style={{ color: '#15354F' }}>
            <option value="">{t('profileSocialLifestyle.select')}</option>
            <option value="Daily">{t('profileSocialLifestyle.daily')}</option>
            <option value="Several times a week">{t('profileSocialLifestyle.severalTimesWeek')}</option>
            <option value="Occasionally">{t('profileSocialLifestyle.occasionally')}</option>
          </select>
        </div>
      )}

      <div className="border-t border-gray-100 my-5" />

      {/* Alcohol */}
      <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileSocial.alcohol')}</p>
      
      <ThreeWayPills value={form.alcohol} onChange={(v) => setForm({...form, alcohol: v, alcoholFrequency: v !== 'Yes' ? '' : form.alcoholFrequency})}
        options={[{ value: 'Yes', label: t('profileSocialLifestyle.yes') }, { value: 'No', label: t('profileSocialLifestyle.no') }, { value: 'Former', label: t('profileSocialLifestyle.former') }]} />
      
      {form.alcohol === 'Yes' && (
        <div className="mb-4">
          <label className="block text-[12px] text-gray-500 mb-1">{t('profileSocialLifestyle.howOften')}</label>
          <select value={form.alcoholFrequency} onChange={(e) => setForm({...form, alcoholFrequency: e.target.value})} className={inputCls} style={{ color: '#15354F' }}>
            <option value="">{t('profileSocialLifestyle.select')}</option>
            <option value="Daily">{t('profileSocialLifestyle.daily')}</option>
            <option value="Several times a week">{t('profileSocialLifestyle.severalTimesWeek')}</option>
            <option value="Weekends">{t('profileSocialLifestyle.weekends')}</option>
            <option value="Socially">{t('profileSocialLifestyle.socially')}</option>
            <option value="Rarely">{t('profileSocialLifestyle.rarely')}</option>
          </select>
        </div>
      )}

      <div className="border-t border-gray-100 my-5" />

      {/* Cannabis */}
      <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileSocial.cannabis')}</p>
      
      <ThreeWayPills value={form.cannabis} onChange={(v) => setForm({...form, cannabis: v, cannabisFrequency: v !== 'Yes' ? '' : form.cannabisFrequency, cannabisMethod: v !== 'Yes' ? '' : form.cannabisMethod})}
        options={[{ value: 'Yes', label: t('profileSocialLifestyle.yes') }, { value: 'No', label: t('profileSocialLifestyle.no') }, { value: 'Former', label: t('profileSocialLifestyle.former') }]} />
      
      {form.cannabis === 'Yes' && (
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileSocialLifestyle.howOften')}</label>
            <select value={form.cannabisFrequency} onChange={(e) => setForm({...form, cannabisFrequency: e.target.value})} className={inputCls} style={{ color: '#15354F' }}>
              <option value="">{t('profileSocialLifestyle.select')}</option>
              <option value="Daily">{t('profileSocialLifestyle.daily')}</option>
              <option value="Several times a week">{t('profileSocialLifestyle.severalTimesWeek')}</option>
              <option value="Occasionally">{t('profileSocialLifestyle.occasionally')}</option>
            </select>
          </div>
          <div>
            <label className="block text-[12px] text-gray-500 mb-1">{t('profileSocialLifestyle.method')}</label>
            <select value={form.cannabisMethod} onChange={(e) => setForm({...form, cannabisMethod: e.target.value})} className={inputCls} style={{ color: '#15354F' }}>
              <option value="">{t('profileSocialLifestyle.select')}</option>
              <option value="Smoking">{t('profileSocialLifestyle.methodSmoking')}</option>
              <option value="Vaping">{t('profileSocialLifestyle.methodVaping')}</option>
              <option value="Edibles">{t('profileSocialLifestyle.methodEdibles')}</option>
              <option value="Tinctures/oils">{t('profileSocialLifestyle.methodTinctures')}</option>
              <option value="Topicals">{t('profileSocialLifestyle.methodTopicals')}</option>
              <option value="Multiple methods">{t('profileSocialLifestyle.methodMultiple')}</option>
            </select>
          </div>
        </div>
      )}

      <div className="border-t border-gray-100 my-5" />

      {/* Exercise */}
      <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileSocial.exercise')}</p>
      
      <div className="mb-4">
        <select value={form.exercise} onChange={(e) => setForm({...form, exercise: e.target.value})} className={inputCls} style={{ color: '#15354F' }}>
          <option value="">{t('profileSocialLifestyle.select')}</option>
          <option value="Never">{t('profileSocialLifestyle.exerciseNever')}</option>
          <option value="Rarely (1-2 times/month)">{t('profileSocialLifestyle.exerciseRarely')}</option>
          <option value="1-2 times per week">{t('profileSocialLifestyle.exercise1to2')}</option>
          <option value="3-4 times per week">{t('profileSocialLifestyle.exercise3to4')}</option>
          <option value="5+ times per week">{t('profileSocialLifestyle.exercise5plus')}</option>
          <option value="Daily">{t('profileSocialLifestyle.exerciseDaily')}</option>
        </select>
      </div>

      <div className="border-t border-gray-100 my-5" />

      {/* Occupation */}
      <p className="text-[11px] font-bold uppercase tracking-widest mb-3 pb-1 inline-block" style={{ color: '#15354F', borderBottom: '1.5px solid #15354F' }}>{t('profileSocial.occupation')}</p>
      
      <div className="mb-4">
        <input type="text" value={form.occupation} onChange={(e) => setForm({...form, occupation: e.target.value})}
          placeholder={t('profileSocialLifestyle.occupationPlaceholder')}
          className="w-full px-3.5 py-2.5 border-[1.5px] border-gray-200 rounded-[14px] text-[13px] focus:outline-none focus:border-[#46A8F9] transition-colors" style={{ color: '#15354F' }} />
      </div>
    </ProfileSubpageLayout>
  )
}
